import { jsx as _jsx } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { Info } from '../../icons.js';
import { cn } from '../../lib/cn.js';
import { FOCUS_RING } from '../../lib/focus-ring.js';
import { Tooltip } from './Tooltip.js';
/**
 * The "why is this here" of a control, one tap or one Tab away.
 *
 * A composition of `Tooltip` rather than a second tooltip: it exists so that a
 * paragraph of explanation can stop being a paragraph. Body text under a
 * control is read once, by the tutor who did not need it, and then pushes the
 * control it explains further down the page every time afterwards.
 *
 * Keyboard reachable because the trigger is a real `<button>` — Radix opens on
 * focus as well as hover and closes on Escape, and points the trigger's
 * `aria-describedby` at the content while it is open. That is the whole reason
 * this is a button and not an `<span tabindex="0">`.
 *
 * `long` is always on: this is never the pill-shaped one-word tooltip.
 *
 * A tap opens it too, and a second tap closes it — a toggletip. A tooltip on
 * its own opens on hover and focus and nothing else, and a touch screen has no
 * hover: a tutor on a phone tapped the button, it took focus, and the
 * explanation never appeared. Radix closes a tooltip on a click, so the
 * trigger's own click decides instead, from whether it was open when the
 * finger went down. A mouse click only ever opens it — the pointer is already
 * hovering, and a click that closed what hovering had just shown would read as
 * broken.
 *
 * Drawn 24px, the size of an aside beside the control it explains, and
 * pressed at 44: the hit area extends 10px past the circle on every side.
 */
export function InfoTooltip({ content, label, side = 'top', className }) {
    const [open, setOpen] = useState(false);
    const wasOpen = useRef(false);
    const pointer = useRef(null);
    return (_jsx(Tooltip, { content: content, side: side, long: true, open: open, onOpenChange: setOpen, children: _jsx("button", { type: "button", "aria-label": label, onPointerDown: (event) => {
                wasOpen.current = open;
                pointer.current = event.pointerType;
            }, onClick: (event) => {
                // Skips Radix's own close-on-click.
                event.preventDefault();
                const via = pointer.current;
                pointer.current = null;
                if (via === 'mouse')
                    setOpen(true);
                else if (via === null)
                    setOpen((current) => !current);
                else
                    setOpen(!wasOpen.current);
            }, className: cn('relative inline-flex size-icon-lg shrink-0 items-center justify-center rounded-full', "before:absolute before:-inset-2.5 before:content-['']", 'text-text-secondary transition-colors duration-fast', 'hover:bg-surface-secondary hover:text-text', FOCUS_RING, className), children: _jsx(Info, { "aria-hidden": "true", className: "size-icon-sm" }) }) }));
}
//# sourceMappingURL=InfoTooltip.js.map